// @contexts/AuthenticationClient.context.tsx
import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import type { AuthenticationClient } from '@services/clients/AuthenticationClient';
import { fromInteropAuthenticationClient } from '@services/clients/AuthenticationClient';
import { createDefaultAuthenticationClient } from "@src/purescript/interop";
import { StubAuthenticationClient } from '@services/stubs/StubAuthenticationClient';
import { enabledFeatures, FeaturesEnum } from '@src/utils/enabledFeatures';
import { requireContext } from '@contexts/requireContext';

const AuthenticationClientContext = createContext<OptionTs<AuthenticationClient>>(Option.none());

export const AuthenticationClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  // Memoize the creation of AuthenticationClient, conditionally using the StubAuthenticationClient if the flag is set
  const authenticationClient = useMemo<AuthenticationClient>(() => {
    if (enabledFeatures[FeaturesEnum.StubClients]) {
      return new StubAuthenticationClient();
    }
    return fromInteropAuthenticationClient(createDefaultAuthenticationClient);
  }, []);

  const contextValue = useMemo(
      () => Option.some(authenticationClient),
      [authenticationClient]
  );

  return (
      <AuthenticationClientContext.Provider value={contextValue}>
        {children}
      </AuthenticationClientContext.Provider>
  );
};

export const useAuthenticationClient = (): AuthenticationClient => {
  return requireContext(
    useContext(AuthenticationClientContext),
    'useAuthenticationClient must be used within an AuthenticationClientProvider',
  );
};
